import { collection, doc, writeBatch, query, where, getDocs, orderBy, getDoc, deleteDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { UserData } from './authService';

// --- واجهات البيانات الجديدة ---

export type ContractStatus = 'نشط' | 'قيد الانتظار' | 'مكتمل' | 'ملغي' | 'معلق' | 'مرفوض';

export interface Contract {
  id?: string;
  clientId: string;
  lawyerId: string;
  lawyerName?: string;  // Added optional lawyerName
  title: string;
  description: string;
  amount: number;
  totalAmount?: number; // Added optional totalAmount
  currency: string;
  status: ContractStatus;
  createdAt: Date;
  dueDate: Date;
}

export interface Payment {
  id?: string;
  contractId: string;
  amount: number;
  dueDate: Date;
  status: 'مدفوع' | 'مستحق' | 'متأخر';
  paymentDate?: Date;  // When the payment was made
  paidDate?: Date;     // Alias for paymentDate for backward compatibility
}

export interface Activity {
  id?: string;
  userId: string;
  type: 'تم الدفع' | 'تم توقيع العقد' | 'تم رفع المستند' | 'تم إنشاء العقد' | 'موافقة على عقد';
  description: string;
  timestamp: Date;
  relatedId: string; // e.g., contractId or paymentId
}

// --- دوال جلب البيانات ---

// جلب الأنشطة لمستخدم معين
export const getActivitiesForUser = async (userId: string): Promise<Activity[]> => {
  console.log(`contractService: getActivitiesForUser called with userId: '${userId}'`);
  const activitiesCollection = collection(db, 'activities');
  // الفرز يتطلب فهرسًا مركبًا. تم تعطيله مؤقتًا لعرض البيانات.
  const q = query(activitiesCollection, where('userId', '==', userId));
  try {
    const querySnapshot = await getDocs(q);
    console.log(`contractService (getActivitiesForUser): Firestore query returned ${querySnapshot.size} documents.`);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return { id: doc.id, ...data, timestamp: data.timestamp.toDate() } as Activity;
    });
  } catch (error) {
    console.error("خطأ في جلب الأنشطة:", error);
    // Firestore يتطلب فهرسًا مركبًا لهذه الاستعلام. سيتم إنشاؤه تلقائيًا عند أول استدعاء أو يمكن إنشاؤه يدويًا في لوحة تحكم Firebase.
    return [];
  }
};

// جلب العقود لمستخدم معين (عميل أو محامي)
export const getContractsForUser = async (userId: string, userRole: 'client' | 'lawyer'): Promise<Contract[]> => {
  console.log(`contractService: getContractsForUser called with userId: '${userId}', role: '${userRole}'`);
  const contractsCollection = collection(db, 'contracts');
  const fieldToQuery = userRole === 'lawyer' ? 'lawyerId' : 'clientId';
  console.log(`contractService: Querying 'contracts' where '${fieldToQuery}' == '${userId}'`);
  
  // استعلام للعقود حيث المستخدم هو العميل أو المحامي
  // الفرز يتطلب فهرسًا مركبًا. تم تعطيله مؤقتًا لعرض البيانات.
  const userContractsQuery = query(contractsCollection, where(fieldToQuery, '==', userId));

  try {
    const querySnapshot = await getDocs(userContractsQuery);
    console.log(`contractService (getContractsForUser): Firestore query returned ${querySnapshot.size} documents.`);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return { id: doc.id, ...data, createdAt: data.createdAt?.toDate() ?? null, dueDate: data.dueDate?.toDate() ?? null } as Contract;
    });
  } catch (error) {
    console.error(`خطأ في جلب عقود لـ ${userRole}:`, error);
    return [];
  }
};

// جلب عقد واحد بواسطة ID
// Update contract status
export const updateContractStatus = async (contractId: string, status: ContractStatus): Promise<void> => {
  try {
    const contractRef = doc(db, 'contracts', contractId);
    await updateDoc(contractRef, {
      status,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating contract status:', error);
    throw error;
  }
};

export const getContractById = async (contractId: string): Promise<Contract | null> => {
  const contractRef = doc(db, 'contracts', contractId);
  const docSnap = await getDoc(contractRef);
  if (!docSnap.exists()) {
    console.error("العقد غير موجود!");
    return null;
  }
  const data = docSnap.data();
  return { id: docSnap.id, ...data, createdAt: data.createdAt.toDate(), dueDate: data.dueDate.toDate() } as Contract;
};

// جلب الدفعات لعقد معين
export const getPaymentsForContract = async (contractId: string): Promise<Payment[]> => {
  const paymentsCollection = collection(db, `contracts/${contractId}/payments`);
  const q = query(paymentsCollection, orderBy('dueDate', 'asc'));
  try {
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return { id: doc.id, ...data, dueDate: data.dueDate.toDate(), paymentDate: data.paymentDate?.toDate() } as Payment;
    });
  } catch (error) {
    console.error("خطأ في جلب الدفعات:", error);
    return [];
  }
};

// تحديث حالة الدفعة
export const updatePaymentStatus = async (contractId: string, paymentId: string, status: 'مدفوع') => {
  const paymentRef = doc(db, `contracts/${contractId}/payments`, paymentId);
  try {
    await updateDoc(paymentRef, {
      status,
      paymentDate: new Date(),
    });
    console.log(`Payment ${paymentId} status updated to ${status}.`);
  } catch (error) {
    console.error('Failed to update payment status:', error);
  }
};

// --- دالة إعداد وتعبئة البيانات التجريبية ---

// دالة مساعدة للبحث عن مستخدم بواسطة البريد الإلكتروني
const findUserByEmail = async (email: string): Promise<UserData | null> => {
  const usersCollection = collection(db, 'users');
  const q = query(usersCollection, where('email', '==', email));
  const querySnapshot = await getDocs(q);
  if (querySnapshot.empty) {
    console.error(`المستخدم ${email} غير موجود في قاعدة البيانات.`);
    return null;
  }
  const userDoc = querySnapshot.docs[0];
  return { uid: userDoc.id, ...userDoc.data() } as UserData;
};

// دالة الإعداد والأتمتة الرئيسية الجديدة
export const setupAndSeedDatabase = async (clientUid: string) => {
  console.log('بدء عملية مسح وإعادة تعبئة البيانات التجريبية...');
  try {
    const lawyer = await findUserByEmail('lawyer@lexpay.sa');
    if (!lawyer?.uid) throw new Error('لم يتم العثور على حساب المحامي.');

    const batch = writeBatch(db);

    // 1. حذف البيانات القديمة (العقود، الدفعات، الأنشطة)
    const collectionsToDelete = ['contracts', 'activities'];
    for (const coll of collectionsToDelete) {
      const oldDocsQuery = query(collection(db, coll), where('clientId', '==', clientUid));
      const oldDocsSnapshot = await getDocs(oldDocsQuery);
      oldDocsSnapshot.forEach(doc => {
        // إذا كان العقد، احذف الدفعات الفرعية أولاً
        if (coll === 'contracts') {
          const paymentsQuery = query(collection(db, `contracts/${doc.id}/payments`));
          getDocs(paymentsQuery).then(paymentSnapshot => {
            paymentSnapshot.forEach(paymentDoc => batch.delete(paymentDoc.ref));
          });
        }
        batch.delete(doc.ref);
      });
    }
    console.log('تم تحديد البيانات القديمة للحذف.');

    // 2. إضافة البيانات الجديدة
    const contractsCollection = collection(db, 'contracts');
    const activitiesCollection = collection(db, 'activities');

    const contractsToSeed = [
      { title: 'مراجعة عقد عمل', amount: 2500, status: 'نشط' as const, daysAgo: 2, dueInDays: 14 },
      { title: 'صياغة عقد تأسيس شركة', amount: 8000, status: 'مكتمل' as const, daysAgo: 15, dueInDays: 30 },
      { title: 'استشارة علامة تجارية', amount: 1500, status: 'نشط' as const, daysAgo: 30, dueInDays: -10 },
    ];

    for (const c of contractsToSeed) {
      const contractRef = doc(contractsCollection);
      const createdAt = new Date(new Date().setDate(new Date().getDate() - c.daysAgo));
      const dueDate = new Date(new Date().setDate(new Date().getDate() + c.dueInDays));

      // إضافة العقد
      batch.set(contractRef, {
        clientId: clientUid, lawyerId: lawyer.uid, title: c.title, description: `وصف تفصيلي لـ: ${c.title}`,
        amount: c.amount, currency: 'ر.س', status: c.status, createdAt, dueDate,
      });

      // إضافة نشاط لإنشاء العقد
      const activityContractRef = doc(activitiesCollection);
      batch.set(activityContractRef, {
        userId: clientUid, type: 'تم توقيع العقد', description: c.title, timestamp: createdAt, relatedId: contractRef.id, clientId: clientUid
      });

      // إضافة دفعات للعقد
      const paymentsRef = collection(db, `contracts/${contractRef.id}/payments`);
      const payment1Ref = doc(paymentsRef);
      const payment2Ref = doc(paymentsRef);
      batch.set(payment1Ref, { contractId: contractRef.id, amount: c.amount / 2, dueDate: createdAt, status: 'مدفوع', paymentDate: createdAt });
      batch.set(payment2Ref, { contractId: contractRef.id, amount: c.amount / 2, dueDate: dueDate, status: 'مستحق' });

      // إضافة نشاط للدفع
      const activityPaymentRef = doc(activitiesCollection);
      batch.set(activityPaymentRef, {
        userId: clientUid, type: 'تم الدفع', description: `دفعة لـ ${c.title}`, timestamp: new Date(new Date().setDate(new Date().getDate() - 1)), relatedId: payment1Ref.id, clientId: clientUid
      });
    }

    // إضافة نشاط رفع مستند (كمثال للعميل)
    const clientActivityDocRef = doc(activitiesCollection);
    batch.set(clientActivityDocRef, {
      userId: clientUid, type: 'تم رفع المستند', description: 'فاتورة رقم INV-00123.pdf', timestamp: new Date(new Date().setDate(new Date().getDate() - 3)), relatedId: 'doc123'
    });

    // إضافة أنشطة وهمية للمحامي
    const lawyerActivity1Ref = doc(activitiesCollection);
    batch.set(lawyerActivity1Ref, {
      userId: lawyer.uid,
      type: 'موافقة على عقد',
      description: `تمت الموافقة على عقد "${contractsToSeed[0].title}" مع العميل.`,
      timestamp: new Date(new Date().setDate(new Date().getDate() - 1)),
      relatedId: 'mock_contract_id_1' // Should be a real ID in a real app
    });

    const lawyerActivity2Ref = doc(activitiesCollection);
    batch.set(lawyerActivity2Ref, {
      userId: lawyer.uid,
      type: 'تم رفع المستند',
      description: 'تم رفع مذكرة الرد في قضية شركة ABC',
      timestamp: new Date(new Date().setDate(new Date().getDate() - 2)),
      relatedId: 'mock_contract_id_2'
    });

    await batch.commit();
    console.log('تمت إضافة البيانات التجريبية الجديدة بنجاح!');
    alert('تم تحديث البيانات التجريبية بنجاح!');
    return { success: true };

  } catch (error) {
    console.error('فشلت عملية الإعداد:', error);
    alert(`فشلت عملية الإعداد: ${error}`);
    throw error;
  }
};
